import React from "react";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

/**
 * Clickable table header that toggles sort asc/desc for the given field.
 * Usage:
 *   <SortableTh field="created_at" sort={sort} onChange={setSort}>Fecha</SortableTh>
 *
 * `sort` is `{field, dir}` where dir is "asc" | "desc".
 * When clicked: same field -> toggle dir; different field -> set field + asc.
 */
export default function SortableTh({ field, sort, onChange, children, align = "left", style = {}, testid, ...rest }) {
    const active = sort?.field === field;
    const dir = active ? sort.dir : null;
    const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;

    const toggle = () => {
        if (!active) onChange({ field, dir: "asc" });
        else onChange({ field, dir: dir === "asc" ? "desc" : "asc" });
    };

    return (
        <th
            {...rest}
            data-testid={testid || `sort-th-${field}`}
            onClick={toggle}
            style={{
                cursor: "pointer",
                userSelect: "none",
                padding: "10px 12px",
                textAlign: align,
                color: active ? "#fff" : "#A3A3A8",
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: ".06em",
                borderBottom: "1px solid #26262B",
                whiteSpace: "nowrap",
                ...style,
            }}
            title={active ? `Ordenado ${dir === "asc" ? "ascendente" : "descendente"} (click para invertir)` : "Click para ordenar"}
        >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                {children}
                <Icon size={12} style={{ opacity: active ? 1 : 0.4 }} />
            </span>
        </th>
    );
}

/**
 * Client-side array sort helper. Accepts optional `getter(row) => value` per field.
 */
export function sortRows(rows, sort, fieldGetters = {}) {
    if (!sort || !sort.field) return rows;
    const getter = fieldGetters[sort.field] || ((r) => r?.[sort.field]);
    const dirMul = sort.dir === "asc" ? 1 : -1;
    const copy = [...rows];
    copy.sort((a, b) => {
        const va = getter(a);
        const vb = getter(b);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (typeof va === "number" && typeof vb === "number") return (va - vb) * dirMul;
        return String(va).localeCompare(String(vb), "es", { numeric: true, sensitivity: "base" }) * dirMul;
    });
    return copy;
}
